<template>
  <div class="account-settings-mobile">
    <h2 class="settings-title">{{ $t('views.account_center.account_settings') }}</h2>

    <section class="mobile-profile">
      <UserAvatarBadge :size="64" :src="avatar" :subscription-key="subscriptionKey" />
      <div class="avatar-actions">
        <el-button type="primary" plain @click="$emit('select-avatar')">
          {{ $t('views.account_center.add_avatar') }}
        </el-button>
        <p v-if="membershipExpiryText" class="membership-expiry">{{ membershipExpiryText }}</p>
      </div>
      <div class="mobile-nickname" :aria-busy="isSavingName">
        <div class="field-label">{{ $t('views.account_center.nickname') }}</div>
        <div v-show="!isEditingName" class="nickname-display">
          <span class="nickname-value">{{ displayName }}</span>
          <button
            type="button"
            class="nickname-edit"
            :aria-label="$t('views.account_center.nickname')"
            @click="$emit('edit-name')"
          >
            <el-icon><EditPen /></el-icon>
          </button>
        </div>
        <el-input
          v-show="isEditingName"
          ref="nicknameInput"
          class="nickname-input"
          :model-value="nickName"
          :aria-label="$t('views.account_center.nickname')"
          :data-placeholder="$t('views.account_center.nickname_placeholder')"
          maxlength="100"
          @update:model-value="$emit('update:nickName', $event)"
          @blur="$emit('save-name')"
          @keyup.enter="$emit('save-name')"
          @keydown.esc.stop.prevent="$emit('cancel-name')"
        />
      </div>
    </section>

    <section class="mobile-email">
      <div class="field-label">{{ $t('views.account_center.email_address') }}</div>
      <p class="email-value">{{ email }}</p>
      <el-button @click="$emit('change-email')">{{ $t('views.account_center.change_email') }}</el-button>
    </section>

    <section class="mobile-devices" :aria-busy="isLoading">
      <div class="device-heading">
        <h3>{{ $t('views.account_center.device_management') }}</h3>
        <p class="device-tip">{{ $t('views.account_center.device_tip') }}</p>
        <el-button @click="$emit('logout-all')">{{ $t('views.account_center.logout_all_devices') }}</el-button>
      </div>
      <el-skeleton v-if="isLoading" animated class="device-skeleton" aria-hidden="true">
        <template #template>
          <div v-for="row in 2" :key="row" class="device-skeleton-row">
            <el-skeleton-item variant="text" class="skeleton-name" />
            <el-skeleton-item variant="text" class="skeleton-detail" />
            <el-skeleton-item variant="text" class="skeleton-detail" />
            <el-skeleton-item variant="button" class="skeleton-action" />
          </div>
        </template>
      </el-skeleton>
      <ul v-else-if="devices.length" class="device-list">
        <li v-for="device in devices" :key="device.device_id" class="device-item">
          <div class="device-name">
            <el-icon><Monitor /></el-icon>
            <span>{{ device.device_name }}</span>
            <el-tag v-if="device.isCurrent" size="small" class="current-device">
              {{ $t('views.account_center.current_device') }}
            </el-tag>
          </div>
          <dl class="device-details">
            <div>
              <dt>{{ $t('views.account_center.last_active_time') }}</dt>
              <dd>{{ device.isCurrent ? $t('views.account_center.now') : device.last_login_time }}</dd>
            </div>
            <div>
              <dt>{{ $t('views.account_center.location') }}</dt>
              <dd>{{ device.ip_address }}</dd>
            </div>
          </dl>
          <el-button
            v-if="device.canLogout"
            class="device-logout"
            @click="$emit('logout-device', device.device_id)"
          >{{ $t('views.account_center.logout') }}</el-button>
        </li>
      </ul>
      <el-empty v-else class="device-empty" />
    </section>

    <section class="mobile-delete-account">
      <el-button type="danger" link @click="$emit('delete-account')">
        {{ $t('views.account_center.delete_my_account') }}
      </el-button>
      <p class="delete-tip">{{ $t('views.account_center.delete_account_tip') }}</p>
    </section>
  </div>
</template>

<script>
import { EditPen, Monitor } from '@element-plus/icons-vue'
import UserAvatarBadge from '@/components/UserAvatarBadge/index.vue'

export default {
  name: 'MobileAccountSettings',
  components: { EditPen, Monitor, UserAvatarBadge },
  props: {
    avatar: { type: String, default: '' },
    subscriptionKey: { type: String, default: '' },
    displayName: { type: String, default: '' },
    email: { type: String, default: '' },
    membershipExpiryText: { type: String, default: '' },
    nickName: { type: String, default: '' },
    isEditingName: Boolean,
    isSavingName: Boolean,
    isLoading: Boolean,
    devices: { type: Array, default: () => [] }
  },
  emits: [
    'update:nickName', 'edit-name', 'save-name', 'cancel-name', 'select-avatar',
    'change-email', 'logout-device', 'logout-all', 'delete-account'
  ],
  methods: {
    getNicknameElement() {
      return this.$refs.nicknameInput?.input
    }
  }
}
</script>

<style scoped lang="scss">
@import '../styles/account_settings';
</style>
